import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useGenerateRiskOut,
  useUpdateHazardRiskReductionStep,
  type RiskAssessmentStep,
  type RiskOutFitAssessment,
  type RiskOutGenerationMode,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  createHazardRiskReductionStepFormSchema,
  defaultCreateHazardRiskReductionStepFormValues,
  type CreateHazardRiskReductionStepFormValues,
} from './CreateHazardRiskReductionStepForm.schema'
import { HazardRiskReductionStepFormFields } from './HazardRiskReductionStepFormFields'
import { applyRiskOutGenerationResult } from './applyRiskOutGenerationResult'
import {
  formatRiskOutDimensionsAboveCapLabels,
  isAnyRiskOutAboveCap,
  listRiskOutDimensionsAboveCap,
} from './riskOutDimensionCap'
import { useHazardRiskReductionStepRiskInCap } from './useHazardRiskReductionStepRiskInCap'

function stepToFormValues(
  step: RiskAssessmentStep
): CreateHazardRiskReductionStepFormValues {
  return {
    riskReductionMeasureId: step.riskReductionMeasureId,
    riskOutInjury: step.riskOutInjury ?? '',
    riskOutFrequency: step.riskOutFrequency ?? '',
    riskOutAvoidance: step.riskOutAvoidance ?? '',
    riskOutLikelihood: step.riskOutLikelihood ?? '',
    riskOutInjuryJustification: step.riskOutInjuryJustification ?? undefined,
    riskOutFrequencyJustification:
      step.riskOutFrequencyJustification ?? undefined,
    riskOutAvoidanceJustification:
      step.riskOutAvoidanceJustification ?? undefined,
    riskOutLikelihoodJustification:
      step.riskOutLikelihoodJustification ?? undefined,
  }
}

export type EditHazardRiskReductionStepSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  hazardId: string
  complianceId: string
  step: RiskAssessmentStep | null
  onSuccess?: () => void
}

export function EditHazardRiskReductionStepSheet({
  open,
  onOpenChange,
  hazardId,
  complianceId,
  step,
  onSuccess,
}: EditHazardRiskReductionStepSheetProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const updateMutation = useUpdateHazardRiskReductionStep(
    hazardId,
    complianceId
  )
  const generateRiskOut = useGenerateRiskOut(hazardId)
  const [fitAssessment, setFitAssessment] =
    useState<RiskOutFitAssessment | null>(null)

  const form = useForm<CreateHazardRiskReductionStepFormValues>({
    resolver: zodResolver(createHazardRiskReductionStepFormSchema),
    defaultValues: defaultCreateHazardRiskReductionStepFormValues,
  })

  useEffect(() => {
    if (open && step) {
      setFitAssessment(null)
      form.reset(stepToFormValues(step))
    }
  }, [open, step, form])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFitAssessment(null)
      if (step) {
        form.reset(stepToFormValues(step))
      }
    }
    onOpenChange(next)
  }

  const selectedRrmId = form.watch('riskReductionMeasureId')
  const riskOutInjury = form.watch('riskOutInjury')
  const riskOutFrequency = form.watch('riskOutFrequency')
  const riskOutAvoidance = form.watch('riskOutAvoidance')
  const riskOutLikelihood = form.watch('riskOutLikelihood')
  const hasAllRiskOutSelected =
    Boolean(riskOutInjury) &&
    Boolean(riskOutFrequency) &&
    Boolean(riskOutAvoidance) &&
    Boolean(riskOutLikelihood)
  const { riskInCap, isRiskOutCapPending } =
    useHazardRiskReductionStepRiskInCap({
      hazardId,
      complianceId,
      excludeStepId: step?.id,
    })
  const hasRiskOutAboveCap =
    hasAllRiskOutSelected &&
    !isRiskOutCapPending &&
    isAnyRiskOutAboveCap(riskInCap, {
      injury: riskOutInjury,
      frequency: riskOutFrequency,
      avoidance: riskOutAvoidance,
      likelihood: riskOutLikelihood,
    })
  const isGenerating = generateRiskOut.isPending

  const isPrimaryActionDisabled =
    updateMutation.isPending ||
    isGenerating ||
    !hasAllRiskOutSelected ||
    hasRiskOutAboveCap

  const showRiskOutCapPrimaryActionTooltip =
    hasRiskOutAboveCap &&
    !updateMutation.isPending &&
    !isGenerating &&
    hasAllRiskOutSelected

  const riskOutDimensionsAboveCap = useMemo(() => {
    if (isRiskOutCapPending || !hasAllRiskOutSelected) {
      return []
    }
    return listRiskOutDimensionsAboveCap(riskInCap, {
      injury: riskOutInjury,
      frequency: riskOutFrequency,
      avoidance: riskOutAvoidance,
      likelihood: riskOutLikelihood,
    })
  }, [
    hasAllRiskOutSelected,
    isRiskOutCapPending,
    riskInCap,
    riskOutAvoidance,
    riskOutFrequency,
    riskOutInjury,
    riskOutLikelihood,
  ])

  const riskOutCapPrimaryActionTooltip = useMemo(() => {
    if (riskOutDimensionsAboveCap.length === 0) {
      return ''
    }
    return t('hazardsPage.hazardDetail.riskFlow.riskOutBlocksSaveTooltip', {
      dimensions: formatRiskOutDimensionsAboveCapLabels(
        riskOutDimensionsAboveCap,
        t
      ),
    })
  }, [riskOutDimensionsAboveCap, t])

  const handleGenerateRiskOut = (mode: RiskOutGenerationMode) => {
    if (!selectedRrmId || !step) {
      return
    }

    const variables =
      mode === 'justifications'
        ? {
            mode,
            riskReductionMeasureId: selectedRrmId,
            excludeStepId: step.id,
            riskOutInjury,
            riskOutFrequency,
            riskOutAvoidance,
            riskOutLikelihood,
          }
        : {
            mode,
            riskReductionMeasureId: selectedRrmId,
            excludeStepId: step.id,
          }

    generateRiskOut.mutate(variables, {
      onSuccess: (result) => {
        setFitAssessment(applyRiskOutGenerationResult(form, mode, result))
      },
      onError: (error) => {
        const message =
          error instanceof Error
            ? error.message
            : t('hazardsPage.hazardDetail.riskFlow.generateRiskOutFailed')
        toast.error(message)
      },
    })
  }

  const onSubmit = form.handleSubmit(
    async (values: CreateHazardRiskReductionStepFormValues) => {
      if (!step) {
        return
      }
      try {
        await updateMutation.mutateAsync({
          stepId: step.id,
          riskReductionMeasureId: values.riskReductionMeasureId,
          riskOutInjury: values.riskOutInjury,
          riskOutFrequency: values.riskOutFrequency,
          riskOutAvoidance: values.riskOutAvoidance,
          riskOutLikelihood: values.riskOutLikelihood,
          riskOutInjuryJustification: values.riskOutInjuryJustification,
          riskOutFrequencyJustification: values.riskOutFrequencyJustification,
          riskOutAvoidanceJustification: values.riskOutAvoidanceJustification,
          riskOutLikelihoodJustification: values.riskOutLikelihoodJustification,
        })
        toast.success(
          t('hazardsPage.hazardDetail.riskFlow.stepUpdated', 'Step saved.')
        )
        setFitAssessment(null)
        handleOpenChange(false)
        onSuccess?.()
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : t(
                'hazardsPage.hazardDetail.riskFlow.editStepFailed',
                'Failed to save step.'
              )
        toast.error(message)
      }
    }
  )

  if (!step) {
    return null
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl lg:max-w-4xl'
      >
        <SheetHeader className='shrink-0 border-b px-4 py-3'>
          <SheetTitle>
            {t(
              'hazardsPage.hazardDetail.riskFlow.editStepDialogTitle',
              'Edit risk reduction measure'
            )}
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className='min-h-0 flex-1'>
          <div className='px-4 py-4'>
            <Form {...form}>
              <form
                id='edit-hazard-risk-reduction-step-form'
                onSubmit={onSubmit}
                className='flex flex-col space-y-6'
              >
                <HazardRiskReductionStepFormFields
                  form={form}
                  hazardId={hazardId}
                  complianceId={complianceId}
                  excludeStepId={step.id}
                  isGenerating={isGenerating}
                  hasAllRiskOutDimensionsSelected={hasAllRiskOutSelected}
                  onGenerateRiskOut={handleGenerateRiskOut}
                  fitAssessment={fitAssessment}
                  onClearFitAssessment={() => setFitAssessment(null)}
                />
              </form>
            </Form>
          </div>
        </ScrollArea>
        <SheetFooter className='shrink-0 flex-row justify-end gap-2 border-t px-4 py-3'>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            {t('common:actions.cancel')}
          </Button>
          {showRiskOutCapPrimaryActionTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className='inline-flex cursor-not-allowed'
                  tabIndex={0}
                  aria-disabled='true'
                >
                  <Button
                    type='submit'
                    form='edit-hazard-risk-reduction-step-form'
                    disabled={isPrimaryActionDisabled}
                  >
                    {t('common:actions.save')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{riskOutCapPrimaryActionTooltip}</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              type='submit'
              form='edit-hazard-risk-reduction-step-form'
              disabled={isPrimaryActionDisabled}
            >
              {t('common:actions.save')}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
